import React, { useState } from 'react';
import { api } from '../../lib/api.js';
import { EditableList } from '../../components/EditableList.jsx';
import { LifecycleTimeline } from './LifecycleTimeline.jsx';

const STATUS_FIELDS = [
  { key: 'text', label: 'Status line', type: 'textarea', full: true },
];
const STEP_FIELDS = [
  { key: 'text', label: 'Step' },
  { key: 'owner', label: 'Owner' },
  { key: 'dueDate', label: 'Due date', type: 'date' },
  { key: 'complete', label: 'Done', type: 'checkbox' },
];
const PLAYBOOK_FIELDS = [
  { key: 'label', label: 'Topic' },
  { key: 'value', label: 'Guidance', type: 'textarea', full: true },
];

const withIds = (arr, make) =>
  arr.map((v, i) => ({ id: String(i), ...make(v) }));

export function ProjectMemoryTab({
  project,
  detail,
  reloadDetail,
  savePayments,
  savingPayments,
  saveSchedule,
  savingSchedule,
}) {
  const memory = detail.memory;
  const [stepsBusy, setStepsBusy] = useState(false);

  // Each card sends only the slice it changed; the endpoint merges it, so two
  // cards saved close together can't overwrite each other's untouched slices.
  async function saveMemory(patch) {
    await api(`/projects/${project.id}/memory`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
    await reloadDetail();
  }

  // The single-step PATCH and the full "next steps" replace both write the
  // same rows, so they must not overlap: this flag disables the list's
  // add/edit/remove while a toggle is in flight, and the list's own busy
  // state disables the checkbox while a replace is in flight.
  async function toggleStep(step) {
    setStepsBusy(true);
    try {
      await api(`/projects/${project.id}/memory/steps/${step.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ complete: !step.complete }),
      });
      await reloadDetail();
    } catch {
      // Best effort — the checkbox stays at its old value and the next
      // successful load reconciles it; nothing false is shown as saved.
    } finally {
      setStepsBusy(false);
    }
  }

  return (
    <div className="memory-tab">
      <p className="tab-intro">
        A living operational handover — enough for someone to take over in a
        minute.
      </p>

      <div className="memory-grid">
        <EditableList
          className="memory-card"
          title="Current status"
          subtitle="What is happening now?"
          items={withIds(memory.currentStatus, (text) => ({ text }))}
          fields={STATUS_FIELDS}
          addLabel="Add status line"
          emptyLabel="Not set."
          toForm={(item) => ({ text: item.text || '' })}
          fromForm={(f) => ({ text: (f.text || '').trim() })}
          keepIf={(p) => p.text}
          onReplace={(rows) =>
            saveMemory({ currentStatus: rows.map((r) => r.text) })
          }
          renderItem={(item) => <p>• {item.text}</p>}
        />

        <LifecycleTimeline
          project={project}
          milestones={memory.lifecycleMilestones}
          payments={detail.paymentMilestones}
          onSaveMilestones={(rows) => saveMemory({ lifecycleMilestones: rows })}
          onSavePayments={savePayments}
          savingPayments={savingPayments}
          onSaveSchedule={saveSchedule}
          savingSchedule={savingSchedule}
        />

        <EditableList
          className="memory-card"
          title="Immediate next steps"
          subtitle="What needs to happen next?"
          items={memory.steps}
          fields={STEP_FIELDS}
          addLabel="Add step"
          emptyLabel="Not set."
          toForm={(step) => ({
            text: step.text || '',
            owner: step.owner || '',
            dueDate: step.dueDate || '',
            complete: Boolean(step.complete),
          })}
          fromForm={(f) => ({
            text: (f.text || '').trim(),
            owner: (f.owner || '').trim(),
            dueDate: f.dueDate || null,
            complete: Boolean(f.complete),
          })}
          keepIf={(p) => p.text}
          externalBusy={stepsBusy}
          onReplace={(rows) => saveMemory({ steps: rows })}
          renderItem={(step, { busy }) => (
            <label className={`memory-step${step.complete ? ' done' : ''}`}>
              <input
                type="checkbox"
                checked={step.complete}
                disabled={busy}
                onChange={() => toggleStep(step)}
              />
              <div>
                <b>{step.text}</b>
                <small>
                  {[step.owner, step.dueDate && `due ${step.dueDate}`]
                    .filter(Boolean)
                    .join(' · ')}
                </small>
              </div>
            </label>
          )}
        />

        <EditableList
          className="memory-card"
          title="Customer playbook"
          subtitle="How do we work with this customer?"
          items={withIds(memory.playbook, ([label, value]) => ({
            label,
            value,
          }))}
          fields={PLAYBOOK_FIELDS}
          addLabel="Add playbook entry"
          emptyLabel="Not set."
          toForm={(item) => ({
            label: item.label || '',
            value: item.value || '',
          })}
          fromForm={(f) => ({
            label: (f.label || '').trim(),
            value: (f.value || '').trim(),
          })}
          keepIf={(p) => p.label && p.value}
          onReplace={(rows) =>
            saveMemory({ playbook: rows.map((r) => [r.label, r.value]) })
          }
          renderItem={(item) => (
            <div className="playbook-row">
              <b>{item.label}</b>
              <span>{item.value}</span>
            </div>
          )}
        />
      </div>
    </div>
  );
}
